import { collection, onSnapshot, query, where, type DocumentData, type QueryDocumentSnapshot } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions, firestoreDb } from './firebase'
import type { AiMagicChatTurn, AiMagicProposalPreview, ProposalSummary } from './aiService'
import { DEFAULT_AI_TIER, mapLegacyOperatingMode, type AiOperatingMode, type AiTierId } from '../types/ai'
import type { AiChatSourceMessage } from '../types/aiChatSource'
import type { Goal } from '../types/timeline'

/**
 * Pending proposal records are deliberately client-cleaned rather than using a
 * Firestore TTL policy: they are presentation state, not an access-control
 * boundary. Phase I may replace this default with the workspace Line policy.
 */
export const DEFAULT_PENDING_PROPOSAL_TTL_MS = 30 * 24 * 60 * 60 * 1000

export type AiChatPendingProposal = {
  turnId: string
  summary: ProposalSummary
  goal: Goal
  meta: AiMagicProposalPreview['meta']
  expiresAt: number
}

const isTierId = (raw: unknown): raw is AiTierId =>
  raw === 'line' || raw === 'line_pro' || raw === 'line_max'

export type AiChatAttachmentKind =
  | 'timeline'
  | 'upload'
  | 'link'
  | 'asset'
  | 'chat'
  | 'form'
  | 'form_responses'
  | 'design'
  | 'spreadsheet'
  | 'document'
  | 'gsheet'

export type AiChatAttachment = {
  id: string
  kind: AiChatAttachmentKind
  label: string
  text?: string
  fileId?: string
  mimeType?: string
  target?: unknown
  channelId?: string
  channelName?: string
  messages?: AiChatSourceMessage[]
  formId?: string
  formResponseIds?: string[]
  formResponseCount?: number
}

export type AiChatSession = {
  id: string
  workspaceId: string
  ownerId: string
  title: string
  status: 'ACTIVE' | 'ARCHIVED'
  operatingMode: AiOperatingMode
  turns: AiMagicChatTurn[]
  attachments: AiChatAttachment[]
  /** Server mirror of the one proposal that can still be Built or Discarded. */
  pendingProposal?: AiChatPendingProposal | null
  /**
   * Tier the switcher was on when this thread was created — used by the
   * switcher hook to rehydrate the same tier when the user reopens the thread.
   * Absent on threads persisted before the tier rollout → read as `'line'`.
   * See docs/pricing/line-tier-integration-plan.md §3b.
   */
  tierAtStart?: AiTierId
  createdAt?: unknown
  updatedAt?: unknown
  lastMessageAt?: unknown
}

const callable = <Input, Output>(name: string) => httpsCallable<Input, Output>(firebaseFunctions, name)
const millis = (value: unknown) => value && typeof value === 'object' && 'toMillis' in value && typeof value.toMillis === 'function'
  ? value.toMillis()
  : 0
const isAttachmentLimitError = (error: unknown) => {
  const code = typeof error === 'object' && error && 'code' in error ? String((error as { code?: unknown }).code ?? '') : ''
  const message = error instanceof Error ? error.message : ''
  return code.includes('resource-exhausted') && /context attachments per chat/i.test(message)
}
const hasTurnAttachmentContexts = (turns: AiMagicChatTurn[]) =>
  turns.some((turn) => Array.isArray(turn.attachmentContexts) && turn.attachmentContexts.length > 0)

const readTierAtStart = (raw: unknown): AiTierId => (isTierId(raw) ? raw : DEFAULT_AI_TIER)

const readPendingProposal = (raw: unknown): AiChatPendingProposal | null => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const candidate = raw as Partial<AiChatPendingProposal>
  if (typeof candidate.turnId !== 'string' || !candidate.turnId.trim()) return null
  if (!Number.isFinite(candidate.expiresAt) || !candidate.summary || !candidate.goal || !candidate.meta) return null
  return candidate as AiChatPendingProposal
}

const mapSession = (snapshot: QueryDocumentSnapshot<DocumentData>): AiChatSession => {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    ...data,
    operatingMode: mapLegacyOperatingMode(data.operatingMode as string | undefined),
    tierAtStart: readTierAtStart((data as { tierAtStart?: unknown }).tierAtStart),
    pendingProposal: readPendingProposal((data as { pendingProposal?: unknown }).pendingProposal),
  } as AiChatSession
}

export const shouldCreateAiChatSession = (input: {
  activeChatId: string | null
}) => !input.activeChatId

export const subscribeAiChatSessions = (workspaceId: string, ownerId: string, onChange: (sessions: AiChatSession[]) => void) =>
  onSnapshot(
    query(collection(firestoreDb, 'ai_chat_sessions'), where('workspaceId', '==', workspaceId), where('ownerId', '==', ownerId)),
    (snapshot) => onChange(snapshot.docs.map(mapSession).sort((a, b) => millis(b.lastMessageAt ?? b.updatedAt) - millis(a.lastMessageAt ?? a.updatedAt))),
    () => onChange([]),
  )

export const createAiChatSession = async (input: {
  workspaceId: string
  operatingMode: AiOperatingMode
  title?: string
  turns: AiMagicChatTurn[]
  attachments: AiChatAttachment[]
  /** Frozen at thread creation. Server persists as-is; may be undefined on legacy callers. */
  tierAtStart?: AiTierId
}) => {
  try {
    return (await callable<typeof input, { id: string }>('createAiChatSession')(input)).data
  } catch (error) {
    if (!input.attachments.length || !hasTurnAttachmentContexts(input.turns) || !isAttachmentLimitError(error)) throw error
    return (await callable<typeof input, { id: string }>('createAiChatSession')({ ...input, attachments: [] })).data
  }
}

export const saveAiChatSession = async (
  input: Pick<AiChatSession, 'id' | 'turns' | 'attachments' | 'operatingMode' | 'status'> & {
    touchHistoryOrder?: boolean
    /** Optional tier update — only pass when the switcher changes mid-thread. */
    tierAtStart?: AiTierId
    /** Omit to retain the current server mirror; pass null to clear it. */
    pendingProposal?: AiChatPendingProposal | null
  },
) => {
  const payload = {
    chatId: input.id,
    turns: input.turns,
    attachments: input.attachments,
    operatingMode: input.operatingMode,
    status: input.status,
    touchHistoryOrder: input.touchHistoryOrder !== false,
    ...(input.tierAtStart ? { tierAtStart: input.tierAtStart } : {}),
    ...(input.pendingProposal !== undefined ? { pendingProposal: input.pendingProposal } : {}),
  }
  try {
    return await callable<typeof payload, { saved: boolean }>('saveAiChatSession')(payload)
  } catch (error) {
    if (!input.attachments.length || !hasTurnAttachmentContexts(input.turns) || !isAttachmentLimitError(error)) throw error
    return callable<typeof payload, { saved: boolean }>('saveAiChatSession')({ ...payload, attachments: [] })
  }
}

export const archiveAiChatSession = (chatId: string) => callable('archiveAiChatSession')({ chatId })
export const renameAiChatSession = (chatId: string, title: string) => callable('renameAiChatSession')({ chatId, title })
export const deleteAiChatSession = (chatId: string) => callable('deleteAiChatSession')({ chatId })

export const inspectAiChatLink = async (workspaceId: string, url: string) =>
  (await callable<{ workspaceId: string; url: string }, { supported: boolean; reason?: string; url?: string; title?: string; contentType?: string; text?: string }>('inspectAiChatLink')({ workspaceId, url })).data
